import { useState } from 'react';

import { api } from '../api/client';
import type { SiteRow } from '../api/types';
import { Badge, Button, Card, Field, Form, Loading, Notice, Select, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';
import { article } from '../vocabulary';
import { TagPicker } from './Tags';

/**
 * One site: its sub-units and its attributes.
 *
 * Attributes are the mechanism that keeps this product generic. Anything a client needs to
 * record about a site that we did not anticipate — a risk tier, a floor area, whether it
 * holds refrigerated stock — is an attribute rather than a column, and rules and scheduling
 * priorities read them by key. SMA had a fixed set of boolean columns for one client's
 * categories, which is why serving a second meant a schema change.
 */

interface SubUnitRow {
  id: string;
  name: string;
  reference: string | null;
  ordinal: number;
}

interface AttributeRow {
  id: string;
  key: string;
  valueType: string;
  valueText: string | null;
  valueNumber: string | null;
  valueBool: boolean | null;
}

export function SiteDetail({
  siteId,
  onBack,
  onEdit,
}: {
  siteId: string;
  onBack: () => void;
  /**
   * Opens the same form the list uses. Supplied only where the caller can edit, so the
   * button is absent rather than present-and-refused for a view-only reader.
   *
   * The coordinates were printed here as text with nothing to change them, and the only
   * way to reach the form was a button on the list behind it — which is where someone
   * looking at a store's location would least think to go back to.
   */
  onEdit?: (() => void) | undefined;
}): JSX.Element {
  const { can, config } = useSession();

  const site = useResource(() => api.get<SiteRow>(`/sites/${siteId}`), [siteId]);
  const subUnits = useResource(() => api.get<SubUnitRow[]>(`/sites/${siteId}/sub-units`), [siteId]);
  const attributes = useResource(() => api.get<AttributeRow[]>(`/sites/${siteId}/attributes`), [siteId]);

  /**
   * The place could not be read — gone, or outside what this caller may see.
   *
   * Everything below depends on it, so nothing below is drawn. Before this the screen said
   * "not found" and then rendered the tag editor underneath it, complete with a working
   * Save button for a place it had just said did not exist; the title also stayed on
   * "Loading…" for good, because only a *successful* load ever replaced it.
   */
  if (!site.loading && site.data === null) {
    return (
      <Card
        title={config.siteLabel}
        actions={<Button variant="secondary" onClick={onBack}>Back to list</Button>}
      >
        <Notice kind="error">
          That {config.siteLabel.toLowerCase()} is not available. It may have been removed,
          or it may not be one of yours.
        </Notice>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={site.data === null ? 'Loading…' : `${site.data.code} — ${site.data.name}`}
        actions={
          <>
            <Button variant="secondary" onClick={onBack}>Back to list</Button>
            {onEdit !== undefined && (
              <Button variant="secondary" onClick={onEdit}>Edit details</Button>
            )}
          </>
        }
      >
        {site.loading ? (
          <Loading />
        ) : site.error !== null ? (
          <Notice kind="error">{site.error}</Notice>
        ) : site.data === null ? null : (
          <div className="row">
            <Badge tone={site.data.isActive ? 'accepted' : 'blocked'}>
              {site.data.isActive ? 'Active' : 'Inactive'}
            </Badge>
            <span className="muted">
              {site.data.latitude === null || site.data.latitude === undefined
                ? 'No coordinates set — visits here cannot be geofenced'
                : `${Number(site.data.latitude).toFixed(5)}, ${Number(site.data.longitude).toFixed(5)} · ${site.data.geofenceRadiusM ?? '—'} m radius`}
            </span>
          </div>
        )}
      </Card>

      <Card title="Who can visit here">
        <p className="muted">
          Tags decide who is scheduled to this {config.siteLabel.toLowerCase()}. With none,
          anyone can be.
        </p>
        <TagPicker ownerId={siteId} kind="site" />
      </Card>

      <Card title={`${config.subUnitLabel}s`}>
        {subUnits.loading ? (
          <Loading />
        ) : subUnits.error !== null ? (
          <Notice kind="error">{subUnits.error}</Notice>
        ) : (
          <>
            <Table
              rows={subUnits.data ?? []}
              keyOf={(row) => row.id}
              empty={`No ${config.subUnitLabel.toLowerCase()}s. The whole site is inspected as one.`}
              columns={[
                { header: '#', numeric: true, render: (row) => row.ordinal },
                { header: 'Name', render: (row) => row.name },
                { header: 'Reference', render: (row) => row.reference ?? '—' },
              ]}
            />
            {can('site.edit') && (
              <SubUnitForm siteId={siteId} label={config.subUnitLabel} onSaved={subUnits.reload} />
            )}
          </>
        )}
      </Card>

      <Card title="Attributes">
        <p className="muted">
          Anything this deployment needs to record about {article(config.siteLabel)}{' '}
          {config.siteLabel.toLowerCase()}.
          Rules and scheduling priorities read these by key, so the key is the part that
          matters — renaming it breaks whatever refers to it.
        </p>

        {attributes.loading ? (
          <Loading />
        ) : attributes.error !== null ? (
          <Notice kind="error">{attributes.error}</Notice>
        ) : (
          <>
            <Table
              rows={attributes.data ?? []}
              keyOf={(row) => row.id}
              empty="No attributes set."
              columns={[
                { header: 'Key', render: (row) => <code>{row.key}</code> },
                { header: 'Type', render: (row) => row.valueType },
                { header: 'Value', render: (row) => describeValue(row) },
              ]}
            />
            {can('site.edit') && <AttributeForm siteId={siteId} onSaved={attributes.reload} />}
          </>
        )}
      </Card>
    </>
  );
}

function describeValue(row: AttributeRow): string {
  switch (row.valueType) {
    case 'NUMBER':
      return row.valueNumber ?? '—';
    case 'BOOLEAN':
      return row.valueBool === null ? '—' : row.valueBool ? 'Yes' : 'No';
    default:
      return row.valueText ?? '—';
  }
}

function SubUnitForm({
  siteId,
  label,
  onSaved,
}: {
  siteId: string;
  label: string;
  onSaved: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [name, setName] = useState('');
  const [reference, setReference] = useState('');

  const submit = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/sites/${siteId}/sub-units`, {
        name,
        ...(reference === '' ? {} : { reference }),
      });
    });
    if (ok) {
      setName('');
      setReference('');
      onSaved();
    }
  };

  return (
    <>
      {error !== null && <Notice kind="error">{error}</Notice>}
      <Form onSubmit={() => void submit()}>
        <div className="form-row">
          <Field label={`Add ${label.toLowerCase()}`} value={name} onChange={setName} required />
          <Field label="Reference" value={reference} onChange={setReference} hint="Optional — a room number, line code, ward name" />
        </div>
        <div className="form-actions">
          <Button type="submit" disabled={busy || name === ''}>
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </Form>
    </>
  );
}

function AttributeForm({ siteId, onSaved }: { siteId: string; onSaved: () => void }): JSX.Element {
  const { run, error, busy } = useAction();
  const [key, setKey] = useState('');
  const [valueType, setValueType] = useState('TEXT');
  const [value, setValue] = useState('');

  const submit = async (): Promise<void> => {
    const body: Record<string, unknown> = { key, valueType };

    // The value goes in the column matching its declared type. Sending it as text
    // regardless would defeat the point of typed columns — a number stored as text cannot
    // be compared with `>` by a rule or summed by a report.
    if (valueType === 'NUMBER') body['valueNumber'] = Number(value);
    else if (valueType === 'BOOLEAN') body['valueBool'] = value === 'true';
    else body['valueText'] = value;

    const ok = await run(async () => {
      await api.put(`/sites/${siteId}/attributes`, body);
    });

    if (ok) {
      setKey('');
      setValue('');
      onSaved();
    }
  };

  return (
    <>
      {error !== null && <Notice kind="error">{error}</Notice>}
      <Form onSubmit={() => void submit()}>
        <div className="form-row">
          <Field label="Key" value={key} onChange={setKey} required hint="e.g. risk_tier, floor_area_m2" />
          <Select
            label="Type"
            value={valueType}
            onChange={(next) => {
              setValueType(next);
              setValue('');
            }}
            required
            options={[
              { value: 'TEXT', label: 'Text' },
              { value: 'NUMBER', label: 'Number' },
              { value: 'BOOLEAN', label: 'Yes / No' },
            ]}
          />
          {valueType === 'BOOLEAN' ? (
            <Select
              label="Value"
              value={value}
              onChange={setValue}
              required
              options={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
            />
          ) : (
            <Field
              label="Value"
              value={value}
              onChange={setValue}
              required
              type={valueType === 'NUMBER' ? 'number' : 'text'}
            />
          )}
        </div>
        <div className="form-actions">
          <Button type="submit" disabled={busy || key === '' || value === ''}>
            {busy ? 'Saving…' : 'Set attribute'}
          </Button>
        </div>
      </Form>
    </>
  );
}
